import React, { useRef, useState } from 'react';
import {
  Box, Button, Card, CardActions, CardContent, CardHeader, Chip, CircularProgress,
  Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, Stack, TextField,
  Tooltip, Typography
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import HistoryIcon from '@mui/icons-material/History';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import { SceneStatus, getStatusColor, getStatusLabel } from '../types/production-review';
import { buildAssetUrl, safeCopy, truncateText } from '../utils';

export default function SceneCard({ scene, review }) {
  const resolvedDirectUrl = scene?.imageUrl || scene?.derivedImageUrl || scene?.imageAsset?.url || scene?.imageAsset?.publicUrl || scene?.imageAsset?.storageUrl || '';
  const assetUrl = resolvedDirectUrl || (
    review?.getAssetUrl
      ? review.getAssetUrl(scene?.imageAsset, scene?.selectedImageAssetId || scene?.imageAssetId)
      : buildAssetUrl(scene?.imageAsset)
  );
  const videoUrl = scene?.videoUrl || scene?.derivedVideoUrl || scene?.videoAsset?.url || scene?.videoAsset?.publicUrl || scene?.videoAsset?.storageUrl || '';
  function isVideoAsset(asset) {
    const kind = String(asset?.kind || '').toLowerCase();
    const mime = String(asset?.mimeType || asset?.mime || '').toLowerCase();
    if (kind === 'video') return true;
    if (mime.startsWith('video/')) return true;
    const name = String(asset?.originalName || asset?.fileName || asset?.name || '').toLowerCase();
    return /\.(mp4|webm|mov|m4v|mkv|avi|3gp|ogv)$/.test(name);
  }
  const isVideo = (isVideoAsset(scene?.videoAsset) && !!videoUrl)
    || (assetUrl && isVideoAsset(scene?.imageAsset));
  const previewUrl = isVideo ? (videoUrl || assetUrl) : assetUrl;
  const busy = review?.isSceneBusy?.(scene?.id);
  const [showPromptDialog, setShowPromptDialog] = useState(false);
  const [promptDraft, setPromptDraft] = useState(String(scene?.imagePrompt || ''));
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const onCopyPrompt = () => {
    if (safeCopy(scene?.imagePrompt || '')) toast.success('Prompt copiado');
    else toast.error('Falha ao copiar');
  };

  const onSavePrompt = async () => {
    await review?.setPromptOverride?.(scene.id, promptDraft);
    setShowPromptDialog(false);
  };

  const onRegenerate = () => review?.regenerate?.(scene.id, { prompt: promptDraft || scene?.imagePrompt || null });

  const onPickFile = () => fileInputRef.current?.click?.();

  const onFileSelected = async (ev) => {
    const file = ev?.target?.files?.[0];
    ev.target.value = '';
    if (!file) return;
    const allowedImages = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
    const allowedVideos = ['video/mp4', 'video/webm', 'video/quicktime', 'video/x-matroska', 'video/x-msvideo', 'video/3gpp', 'video/x-m4v', 'video/ogg'];
    const allowed = [...allowedImages, ...allowedVideos];
    const mime = String(file.type || '').toLowerCase();
    if (!allowed.includes(mime) && !mime.startsWith('image/') && !mime.startsWith('video/')) {
      toast.error('Formato inválido. Use PNG, JPEG, WEBP, MP4, WEBM, MOV ou M4V.');
      return;
    }
    if (file.size > 250 * 1024 * 1024) {
      toast.error('Arquivo excede 250 MB.');
      return;
    }
    setUploading(true);
    try {
      await review?.uploadManual?.(scene.id, file);
    } finally {
      setUploading(false);
    }
  };

  const isApproved = String(scene?.sceneStatus || '') === SceneStatus.APPROVED
    || String(scene?.sceneStatus || '') === SceneStatus.MANUAL_IMAGE;

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
      {(busy || uploading) && (
        <Box
          sx={{
            position: 'absolute', inset: 0, zIndex: 10,
            bgcolor: 'rgba(0,0,0,0.45)', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            borderRadius: 1
          }}
        >
          <CircularProgress size={36} />
        </Box>
      )}
      <CardHeader
        avatar={<Chip size="small" label={`#${String(scene?.orderIndex ?? scene?.id).padStart(2, '0')}`} />}
        action={<Chip size="small" label={getStatusLabel(scene?.sceneStatus)} color={getStatusColor(scene?.sceneStatus)} />}
        titleTypographyProps={{ variant: 'subtitle2' }}
      />
      <Box
        sx={{
          aspectRatio: '16 / 9', width: '100%',
          bgcolor: 'action.hover', display: 'flex', alignItems: 'center', justifyContent: 'center',
          overflow: 'hidden'
        }}
      >
        {previewUrl
          ? (
            isVideo
              ? (
                <Box
                  component="video"
                  src={previewUrl}
                  controls
                  preload="metadata"
                  playsInline
                  sx={{ width: '100%', height: '100%', objectFit: 'cover', bgcolor: '#000' }}
                  title={`Cena ${scene?.orderIndex}`}
                />
              )
              : (
                <Box
                  component="img"
                  src={previewUrl}
                  alt={`Cena ${scene?.orderIndex}`}
                  loading="lazy"
                  sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  onClick={() => review?.setPreviewImage?.(previewUrl)}
                  style={{ cursor: 'zoom-in' }}
                />
              )
          )
          : <Typography color="text.disabled" variant="body2">Sem mídia</Typography>}
      </Box>
      <CardContent sx={{ flexGrow: 1, pb: 1, '&:last-child': { pb: 1 } }}>
        <Stack spacing={1.2} sx={{ mt: 0.5 }}>
          <Box>
            <Typography variant="caption" color="text.secondary">Narração</Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{truncateText(scene?.text, 140) || '-'}</Typography>
          </Box>
          <Divider />
          <Box>
            <Typography variant="caption" color="text.secondary">Prompt{scene?.manualPromptOverride ? ' (editado)' : ''}</Typography>
            <Typography variant="caption" sx={{ whiteSpace: 'pre-wrap' }}>{truncateText(scene?.imagePrompt, 140) || '-'}</Typography>
          </Box>
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            <Chip size="small" label={`Versões: ${String(Array.isArray(scene?.versions) ? scene.versions.length : 0)}`} variant="outlined" />
            <Chip size="small" label={`Ativa: v${String((() => {
              const arr = Array.isArray(scene?.versions) ? scene.versions : [];
              const sel = Number(scene?.selectedVersionId) || 0;
              const found = arr.find((v) => Number(v.id) === sel);
              return found?.versionNumber || (arr.length ? arr[arr.length - 1]?.versionNumber : '-');
            })())}`} variant="outlined" />
            {isApproved && scene?.approvedAt && (
              <Chip size="small" icon={<CheckCircleIcon fontSize="x-small" />} label="Aprovado" color="success" variant="filled" />
            )}
          </Stack>
        </Stack>
      </CardContent>
      <Divider />
      <CardActions sx={{ flexWrap: 'wrap', justifyContent: 'flex-start', p: 1 }}>
        <Button
          size="small"
          variant="contained"
          color="success"
          startIcon={<CheckCircleIcon fontSize="small" />}
          disabled={busy || uploading}
          onClick={() => review?.approveScene?.(scene.id)}
        >Aprovar</Button>
        <Tooltip title="Editar prompt">
          <Button
            size="small"
            variant="outlined"
            startIcon={<EditIcon fontSize="small" />}
            disabled={busy || uploading}
            onClick={() => { setPromptDraft(String(scene?.imagePrompt || '')); setShowPromptDialog(true); }}
          >Editar Prompt</Button>
        </Tooltip>
        <Tooltip title="Regenerar com prompt atual">
          <Button
            size="small"
            variant="outlined"
            startIcon={<RefreshIcon fontSize="small" />}
            disabled={busy || uploading}
            onClick={onRegenerate}
          >Regenerar</Button>
        </Tooltip>
        <Tooltip title="Upload manual (PNG/JPEG/WEBP/MP4/WEBM/MOV)">
          <Button
            size="small"
            variant="outlined"
            startIcon={<UploadFileIcon fontSize="small" />}
            disabled={busy || uploading}
            onClick={onPickFile}
          >Upload Manual</Button>
        </Tooltip>
        <Tooltip title="Visualizar mídia">
          <IconButton size="small" disabled={busy || uploading || !previewUrl} onClick={() => previewUrl && review?.setPreviewImage?.(previewUrl)}>
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Copiar prompt">
          <IconButton size="small" disabled={busy || uploading} onClick={onCopyPrompt}>
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Ver versões">
          <IconButton size="small" disabled={busy || uploading} onClick={() => review?.setVersionDrawerScene?.(scene)}>
            <HistoryIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </CardActions>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/jpg,image/webp,video/mp4,video/webm,video/quicktime,video/x-matroska,video/x-msvideo,video/3gpp,video/x-m4v,video/ogg,image/*,video/*"
        style={{ display: 'none' }}
        onChange={onFileSelected}
      />

      <Dialog open={showPromptDialog} onClose={() => setShowPromptDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Editar Prompt — Cena #{String(scene?.orderIndex ?? scene?.id).padStart(2, '0')}
          <IconButton onClick={() => setShowPromptDialog(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Prompt de geração"
              multiline
              minRows={6}
              maxRows={14}
              fullWidth
              value={promptDraft}
              onChange={(e) => setPromptDraft(e.target.value)}
              helperText="Ao clicar em Regenerar, este prompt será usado como override."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPromptDialog(false)}>Cancelar</Button>
          <Button onClick={onSavePrompt} variant="outlined">Salvar Apenas</Button>
          <Button color="primary" variant="contained" onClick={async () => {
            await review?.setPromptOverride?.(scene.id, promptDraft);
            setShowPromptDialog(false);
            await review?.regenerate?.(scene.id, { prompt: promptDraft || scene?.imagePrompt || null });
          }} startIcon={<RefreshIcon fontSize="small" />}>
            Salvar e Regenerar
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
